import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Download } from "lucide-react";
import { MaskedLine, EASE } from "./Reveal";
import { scrollToId } from "./Header";
import { openBrochureModal } from "./BrochureModal";
import { HERO_IMAGE } from "@/data/apartments";
import { useLang } from "@/i18n";

export const KineticHero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const go = (e, href) => {
    e.preventDefault();
    scrollToId(href);
  };

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden bg-ink" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
          src={HERO_IMAGE}
          alt={t.hero.alt}
          className="h-[115%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/25 to-ink/85" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex min-h-screen max-w-[1440px] flex-col justify-end px-6 pb-60 pt-28 sm:px-10 sm:pb-28 lg:pb-32"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="mb-6 text-xs font-bold uppercase tracking-[0.28em] text-ivory/95 [text-shadow:0_1px_10px_rgba(0,0,0,0.7)]"
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1
          className="font-serif text-5xl font-medium leading-[1.05] tracking-tight text-ivory [text-shadow:0_2px_24px_rgba(0,0,0,0.55)] sm:text-7xl lg:text-[6rem]"
          data-testid="hero-title"
        >
          <MaskedLine delay={0.35}>{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.52}>
            <span className="text-sun">{t.hero.line2}</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
          className="mt-8 max-w-md text-base font-medium leading-relaxed text-ivory [text-shadow:0_1px_12px_rgba(0,0,0,0.6)] sm:text-lg"
          data-testid="hero-subtitle"
        >
          {t.hero.subtitle}
          <span className="mt-2 block font-semibold text-sun">{t.hero.subtitle2}</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contatti"
            onClick={(e) => go(e, "#contatti")}
            data-testid="hero-cta-primary"
            className="rounded-full bg-terracotta px-9 py-4 text-xs font-bold uppercase tracking-[0.2em] text-ivory shadow-[0_10px_35px_rgba(194,105,12,0.5)] transition-all duration-300 hover:scale-[1.03] hover:bg-sun hover:text-ink"
          >
            {t.hero.ctaPrimary}
          </a>
          <button
            onClick={openBrochureModal}
            data-testid="hero-cta-brochure"
            className="flex items-center gap-2.5 rounded-full bg-ivory px-9 py-4 text-xs font-bold uppercase tracking-[0.2em] text-ink shadow-[0_10px_35px_rgba(0,0,0,0.3)] transition-all duration-300 hover:scale-[1.03] hover:bg-sun"
          >
            <Download className="h-4 w-4" strokeWidth={2} />
            {t.brochure}
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={(e) => go(e, "#progetto")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-8 right-8 z-10 hidden items-center gap-3 text-ivory/70 transition-colors hover:text-ivory sm:flex"
        data-testid="hero-scroll-indicator"
        aria-label={t.hero.scroll}
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <ArrowDown className="h-4 w-4 animate-bounce" strokeWidth={1.25} />
      </motion.button>
    </section>
  );
};
